/**
 * Worklist case-level severity — PROTOTYPE classification (frontend-only).
 *
 * Per-finding clinical severity is NOT present in the worklist list payload:
 * `/api/reader/cohort` returns detected-condition strings only, and the real
 * `category` ("acute" | "actionable" | "incidental" | "routine") rides on
 * per-prediction `get_study_bbox` data that the worklist fetches lazily, one
 * case at a time. To drive a case-level severity badge + sort across the WHOLE
 * list without an N-case bbox fan-out, we classify each detected condition
 * string with the keyword rules below and take the HIGHEST severity per case.
 *
 * This is an explicit, tunable placeholder — NOT a clinical source of truth.
 * Replace the keyword lists (or swap to an exact `condition -> severity` map)
 * once the backend surfaces a real per-study severity on the cohort payload.
 * We intentionally only ever mark "acute" / "actionable"; everything else is
 * left unmarked (returns null) per product spec.
 *
 * SOURCE-OF-TRUTH NOTE: the authoritative per-finding `category` lives on
 * `get_study_bbox` and is what the case-viewer's FindingsPanel renders. This
 * keyword badge can therefore disagree with the real category once a case is
 * opened — when they differ, the in-viewer `category` wins; this badge is only
 * an approximate worklist triage hint. Backing the badge with the real category
 * (and retiring this classifier) is tracked in issue #644.
 */

export type Severity = "acute" | "actionable";

/**
 * Where a row's severity came from. `keyword` = the prototype classifier below
 * (a guess from condition names). `tier` = the device's own clinical_tier,
 * which reporter/surfacer rows carry on each surfaced finding — no guess, so
 * those rows are labelled with the clinical-tier names the rest of the app uses.
 */
export type SeveritySource = "keyword" | "tier";

// clinical_tier → severity. Only the two billable tiers mark a case; the
// surfacer never surfaces anything else.
const TIER_SEVERITY: Readonly<Record<string, Severity>> = {
  acute: "acute",
  prompt_workup: "actionable",
};

/** Highest severity across a reporter case's surfaced-finding tiers, or null
 * when none carries a billable tier (including rows whose tier is unknown). */
export function tierCaseSeverity(tiers: readonly (string | null | undefined)[]): Severity | null {
  let best: Severity | null = null;
  for (const tier of tiers) {
    const s = tier ? TIER_SEVERITY[tier] : undefined;
    if (s && (best === null || SEVERITY_RANK[s] > SEVERITY_RANK[best])) best = s;
  }
  return best;
}

// Higher number = more severe. Used by both the badge precedence (highest
// finding wins) and the worklist "Severity" sort.
export const SEVERITY_RANK: Record<Severity, number> = {
  acute: 2,
  actionable: 1,
};

// Substrings that mark a condition acute (red). Matched case-insensitively
// against the full "organ/finding" condition string.
const ACUTE_KEYWORDS = [
  "acute",
  "hemorrhage",
  "haemorrhage",
  "rupture",
  "ruptured",
  "dissection",
  "embolism",
  "embolus",
  "pneumothorax",
  "perforation",
  "free_air",
  "free_fluid",
  "ischemia",
  "ischaemia",
  "infarct",
  "obstruction",
  "aneurysm",
  "active_bleed",
  "extravasation",
  "torsion",
];

// Substrings that mark a condition actionable (orange) when no acute keyword
// matched. Findings that match neither list are left unmarked.
const ACTIONABLE_KEYWORDS = [
  "nodule",
  "mass",
  "lesion",
  "stenosis",
  "stone",
  "calculus",
  "fracture",
  "effusion",
  "lymphadenopathy",
  "dilatation",
  "dilated",
  "complex_cyst",
];

/** Classify a single condition ("organ/finding") → severity, or null. */
export function findingSeverity(condition: string): Severity | null {
  const f = condition.toLowerCase();
  if (ACUTE_KEYWORDS.some((k) => f.includes(k))) return "acute";
  if (ACTIONABLE_KEYWORDS.some((k) => f.includes(k))) return "actionable";
  return null;
}

/** Highest severity across a case's detected conditions, or null if none mark. */
export function caseSeverity(conditions: readonly string[]): Severity | null {
  let best: Severity | null = null;
  for (const c of conditions) {
    const s = findingSeverity(c);
    if (s && (best === null || SEVERITY_RANK[s] > SEVERITY_RANK[best])) best = s;
  }
  return best;
}

/** Numeric rank for sorting; unmarked cases sink below actionable. */
export function severityRank(severity: Severity | null): number {
  return severity ? SEVERITY_RANK[severity] : 0;
}

/**
 * Comparator that orders "organ/finding" conditions by severity — acute first,
 * then actionable, then unmarked. Stable when passed to Array.prototype.sort
 * (the input order is preserved within each severity tier), so a case's finding
 * list reads acute → actionable → the rest without otherwise reshuffling it.
 */
export function compareFindingSeverity(a: string, b: string): number {
  return severityRank(findingSeverity(b)) - severityRank(findingSeverity(a));
}
